import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import type { DataLibrarySessionFeedbackRow, DataLibrarySurveyQuestion } from '../../../types';
import { SurveyResponsePill } from '../components/SurveyResponsePill';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { GroupBadge } from '../../common';
import { renderCohortHealthBadge } from '../components/CohortHealthBadge';

export function renderSessionFeedbackCell(
  row: DataLibrarySessionFeedbackRow,
  columnId: string,
  questions: DataLibrarySurveyQuestion[],
): React.ReactNode {
  switch (columnId) {
    case 'email':
      return (
        <ParticipantEmailPill
          email={row.email}
          participantId={row.participant_id}
          cohortId={row.cohort_id}
        />
      );
    case 'full_name':
      return <Text size="sm">{row.full_name}</Text>;
    case 'session_number':
      return <Text size="sm">{row.session_number}</Text>;
    case 'session_topic':
      return <Text size="sm">{row.session_topic}</Text>;
    case 'session_date':
      return (
        <Text size="sm">
          {row.session_date
            ? new Date(row.session_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '—'}
        </Text>
      );
    case 'coach_name':
      return <Text size="sm">{row.coach_name || '—'}</Text>;
    case 'date_submitted':
      return (
        <Text size="sm">
          {row.date_submitted
            ? new Date(row.date_submitted).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '—'}
        </Text>
      );
    case 'cohort_name':
      return <Text size="sm">{row.cohort_name}</Text>;
    case 'cohort_health':
      return renderCohortHealthBadge(row.cohort_health);
    case 'group_name':
      return <GroupBadge groupName={row.group_name} groupId={row.group_id} />;
    case 'overall_satisfaction':
      if (!row.overall_satisfaction) {
        return <Text size="xs" c="dimmed">—</Text>;
      }
      return <SurveyResponsePill value={row.overall_satisfaction} questionType="scale" />;
    case 'attendance_rate':
      if (row.attendance_rate === null) {
        return <Text size="sm" c="dimmed">&mdash;</Text>;
      }
      {
        const pct = row.attendance_rate;
        const color = pct >= 80 ? 'medleyGreen' : pct >= 60 ? 'orange' : 'dangerRed';
        return (
          <Tooltip label={`${pct}% attendance`} withArrow>
            <Group gap={6} wrap="nowrap">
              <Progress value={pct} color={color} size="sm" w={50} />
              <Text size="xs" c="dimmed">{pct}%</Text>
            </Group>
          </Tooltip>
        );
      }
    case 'avg_engagement':
      return (
        <Text size="sm">
          {row.avg_engagement !== null ? Number(row.avg_engagement).toFixed(1) : <>&mdash;</>}
        </Text>
      );
    case 'has_open_flag':
      return row.has_open_flag ? (
        <Badge size="xs" color="warningAmber" variant="light">Flagged</Badge>
      ) : (
        <Text size="sm" c="dimmed">&mdash;</Text>
      );
    default: {
      // Dynamic question columns (prefixed with q_)
      if (columnId.startsWith('q_')) {
        const questionId = columnId.slice(2);
        const value = row.responses[questionId];
        const question = questions.find(q => q.question_id === questionId);

        if (!value) {
          return <Text size="xs" c="dimmed">—</Text>;
        }

        return (
          <SurveyResponsePill
            value={value}
            questionType={question?.question_type || 'text'}
          />
        );
      }
      return <Text size="sm">—</Text>;
    }
  }
}
